import { makeAutoObservable, action, runInAction } from "mobx";

interface INoVNCContainerStore {
  isConnected: boolean;
  isLoading: boolean;
  loadingProgress: number;
  isReconnecting: boolean;
  showReconnectUI: boolean;
  isDrawerOpen: boolean;
  isFullscreen: boolean;
  brightness: number;
  qualityLevel: number;
  compressionLevel: number;
  remainingTime: number | null;
  runningUntil: Date | null;
  url: string;
  sendClipboardText: (text: string) => void;
}

class NoVNCContainerStore implements INoVNCContainerStore {
  isConnected = false;
  isLoading = true;
  loadingProgress = 0;
  isReconnecting = false;
  showReconnectUI = false;
  isDrawerOpen = false;
  isFullscreen = false;
  brightness = 1;
  qualityLevel = 6;
  compressionLevel = 2;
  remainingTime: number | null = null;
  runningUntil: Date | null = null;
  url = "";
  sendClipboardText: (text: string) => void = () => {};

  constructor() {
    makeAutoObservable(this, {
      setConnected: action.bound,
      setLoading: action.bound,
      setLoadingProgress: action.bound,
      setReconnecting: action.bound,
      setShowReconnectUI: action.bound,
      toggleDrawer: action.bound,
      toggleFullscreen: action.bound,
      setBrightness: action.bound,
      setQualityLevel: action.bound,
      setCompressionLevel: action.bound,
      updateRunningUntil: action.bound,
    });
  }

  setConnected = (status: boolean) => {
    this.isConnected = status;
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setLoadingProgress = (progress: number) => {
    this.loadingProgress = Math.floor(progress);
  };

  setReconnecting = (status: boolean) => {
    this.isReconnecting = status;
  };

  setShowReconnectUI = (status: boolean) => {
    this.showReconnectUI = status;
  };

  toggleDrawer = () => {
    this.isDrawerOpen = !this.isDrawerOpen;
  };

  toggleFullscreen = () => {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        (document.documentElement as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  setBrightness = (value: number) => {
    this.brightness = value;
  };

  setQualityLevel = (value: number) => {
    this.qualityLevel = value;
  };

  setCompressionLevel = (value: number) => {
    this.compressionLevel = value;
  };

  updateRunningUntil = (newRunningUntil: string | number) => {
    if (typeof newRunningUntil === "number") {
      this.runningUntil = new Date(newRunningUntil * 1000);
    } else if (typeof newRunningUntil === "string") {
      const timestamp = Number(newRunningUntil);
      if (!isNaN(timestamp)) {
        this.runningUntil = new Date(timestamp * 1000);
      } else {
        this.runningUntil = new Date(newRunningUntil);
      }
    } else {
      this.runningUntil = null;
    }
  };

  calculateRemainingTime = () => {
    if (this.runningUntil) {
      const now = new Date();
      const diff = (this.runningUntil.getTime() - now.getTime()) / 1000;
      this.remainingTime = Math.max(0, Math.floor(diff));
    } else {
      this.remainingTime = null;
    }
  };
}

export const noVNCContainerStore = new NoVNCContainerStore();
export default noVNCContainerStore;
